import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Avatar, Button } from '@chillout/ui'
import { AdminModal } from '../shared/Modal/AdminModal'
import { ModalActions } from '../shared/Modal/ModalActions'
import type { RecentActivityEntry } from './recentActivityConstants'

interface RecentActivityDetailModalProps {
  entry: RecentActivityEntry | null
  onClose: () => void
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View className="mt-5">
      <Text className="text-xs font-bold uppercase tracking-[0.8px] text-secondary">{label}</Text>
      <View className="mt-1">{children}</View>
    </View>
  )
}

function formatActorDisplay(name: string | null, role: string | null, t: (key: string) => string): string {
  if (!name || role === 'system') return t('recentActivity.detail.system')
  if (role === 'manager') return `${name} (${t('recentActivity.detail.managerRole')})`
  if (role === 'admin') return `${name} (${t('recentActivity.detail.adminRole')})`
  return name
}

function formatDelta(entry: RecentActivityEntry, t: (key: string) => string): string | null {
  if (entry.stampsDelta !== 0) {
    const sign = entry.stampsDelta > 0 ? '+' : ''
    const abs = Math.abs(entry.stampsDelta)
    const word = abs === 1 ? t('common.activity.stampSingle') : t('common.activity.stamps')
    return `${sign}${entry.stampsDelta} ${word}`
  }
  if (entry.rewardsDelta !== 0) {
    const sign = entry.rewardsDelta > 0 ? '+' : ''
    const abs = Math.abs(entry.rewardsDelta)
    const word = abs === 1 ? t('common.activity.rewardSingle') : t('common.activity.rewards')
    return `${sign}${entry.rewardsDelta} ${word}`
  }
  return null
}

export function RecentActivityDetailModal({ entry, onClose }: RecentActivityDetailModalProps) {
  const { t, i18n } = useTranslation()

  if (!entry) return null

  const date = new Date(entry.createdAt)
  const timestamp = date.toLocaleString(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  const activityLabel = entry.type === 'new_member_joined'
    ? t('recentActivity.activityTypes.new_member_joined')
    : t(`common.activity.type.${entry.type}`)

  const delta = formatDelta(entry, t)

  return (
    <AdminModal
      visible
      onClose={onClose}
      closeAccessibilityLabel={t('recentActivity.detail.closeAccessibilityLabel')}
      title={activityLabel}
      subtitle={timestamp}
      actions={
        <ModalActions>
          <Button
            label={t('recentActivity.detail.close')}
            variant="secondary"
            onPress={onClose}
          />
        </ModalActions>
      }
    >
      <DetailRow label={t('recentActivity.detail.memberLabel')}>
        <View className="flex-row items-center gap-3">
          <Avatar name={entry.memberName} email={entry.memberEmail} size={36} />
          <View className="min-w-0 flex-1">
            <Text className="text-sm font-semibold text-primary">{entry.memberName}</Text>
            <Text className="text-xs font-normal text-secondary" numberOfLines={1}>{entry.memberEmail}</Text>
            {entry.memberDisplayId ? (
              <Text className="text-xs font-normal text-muted">{entry.memberDisplayId}</Text>
            ) : null}
          </View>
        </View>
      </DetailRow>

      {entry.type !== 'new_member_joined' ? (
        <DetailRow label={t('recentActivity.detail.performedByLabel')}>
          <Text className="text-sm font-normal text-primary">
            {formatActorDisplay(entry.actorName, entry.actorRole, t)}
          </Text>
        </DetailRow>
      ) : null}

      {delta ? (
        <DetailRow label={t('recentActivity.detail.changeSummaryLabel')}>
          <Text className={`text-base font-semibold ${entry.stampsDelta < 0 || entry.rewardsDelta < 0 ? 'text-accent' : 'text-primary'}`}>
            {delta}
          </Text>
          {entry.rewardItemName ? (
            <Text className="mt-0.5 text-sm font-normal text-secondary">{entry.rewardItemName}</Text>
          ) : null}
        </DetailRow>
      ) : null}

      {entry.receiptId ? (
        <DetailRow label={t('recentActivity.detail.receiptLabel')}>
          <Text className="text-sm font-normal text-primary">{entry.receiptId}</Text>
        </DetailRow>
      ) : null}

      {entry.trigger === 'target_reduction' ? (
        <DetailRow label={t('recentActivity.detail.targetReductionLabel')}>
          {entry.stampTargetBefore != null && entry.stampTargetAfter != null ? (
            <Text className="text-sm font-normal text-primary">
              {t('recentActivity.detail.targetReductionStampsPerCard', { targetBefore: entry.stampTargetBefore, targetAfter: entry.stampTargetAfter })}
            </Text>
          ) : null}
          {entry.stampsBeforeReduction != null && entry.stampsAfterReduction != null ? (
            <Text className="mt-0.5 text-sm font-normal text-primary">
              {t('recentActivity.detail.targetReductionMemberStamps', { before: entry.stampsBeforeReduction, after: entry.stampsAfterReduction })}
            </Text>
          ) : null}
        </DetailRow>
      ) : null}

      {entry.correctionReason ? (
        <DetailRow label={t('recentActivity.detail.reasonLabel')}>
          <Text className="text-sm font-normal text-primary">
            {t(`common.adjustmentReasons.${entry.correctionReason}` as never)}
          </Text>
        </DetailRow>
      ) : null}

      {entry.correctionNote ? (
        <DetailRow label={t('recentActivity.detail.noteLabel')}>
          <Text className="text-sm font-normal text-primary">{entry.correctionNote}</Text>
        </DetailRow>
      ) : null}

      {entry.deviceName ? (
        <DetailRow label={t('recentActivity.detail.deviceLabel')}>
          <Text className="text-sm font-normal text-primary">{entry.deviceName}</Text>
        </DetailRow>
      ) : null}
    </AdminModal>
  )
}